import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup } from 'react-bootstrap';
import moment from 'moment';
import { getRequest, postRequest } from '../../../utils/api';
import { showToast } from '../../../utils/toast'; // Adjust path to your toast utility
interface AssignPlanModalProps {
  show: boolean;
  onHide: () => void;
  user: any;
  onSuccess: () => void; // Changed from onSave to onSuccess to trigger a list refresh in the parent component
}

// Interface for the discount modal state
interface DiscountDetails {
  isCustomDiscountChecked: boolean;
  discountType: 'amount' | 'percentage';
  discountValue: number | '';
  discountReason: string;
}

const AssignPlanModal = ({ show, onHide, user, onSuccess }: AssignPlanModalProps) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [term, setTerm] = useState("monthly"); // 'monthly', 'quarterly', 'yearly'
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState("");

  // Custom Discount States
  const [customDiscount, setCustomDiscount] = useState<number>(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountDetails, setDiscountDetails] = useState<DiscountDetails>({
    isCustomDiscountChecked: false,
    discountType: 'amount',
    discountValue: '',
    discountReason: ''
  });
  const [isSaving, setIsSaving] = useState(false); // NEW: Loading state for the API call
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [taxType, setTaxType] = useState('IGST'); // NEW: Track tax type
  // --- DYNAMIC MATH CALCULATIONS ---
  const selectedPlanDetails = plans.find(p => p._id === selectedPlan);

  const monthlyPrice = Number(selectedPlanDetails?.plan_selling_price) || 0;
  const yearlyDiscount = Number(selectedPlanDetails?.plan_year_discount) || 0;
  const quarterlyDiscount = Number(selectedPlanDetails?.plan_quarter_discount) || 0;

  let grossAmount = 0;
  let termDiscount = 0;

  if (term === 'monthly') {
    grossAmount = monthlyPrice;
  } else if (term === 'quarterly') {
    grossAmount = monthlyPrice * 3;
    termDiscount = quarterlyDiscount;
  } else if (term === 'yearly') {
    grossAmount = monthlyPrice * 12;
    termDiscount = yearlyDiscount;
  }

  // Fetch plans & reset defaults when opened
  useEffect(() => {
    if (show && user) {
      fetchPlans();
      setStartDate(moment().format('YYYY-MM-DD'));
      setTerm("monthly");
      setCustomDiscount(0);
      // Reset discount modal state
      setDiscountDetails({
        isCustomDiscountChecked: false,
        discountType: 'amount',
        discountValue: '',
        discountReason: ''
      });
    } else {
      setSelectedPlan("");
    }
  }, [show, user]);

  // Auto-calculate End Date based on Start Date and Billing Cycle
  useEffect(() => {
    if (startDate) {
      let monthsToAdd = 1;
      if (term === 'quarterly') monthsToAdd = 3;
      if (term === 'yearly') monthsToAdd = 12;

      setEndDate(moment(startDate).add(monthsToAdd, 'months').format('YYYY-MM-DD'));
    } else {
      setEndDate("");
    }
  }, [startDate, term]);

  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const res = await getRequest(`/user/planPricingDropdown?user_id=${user._id}`);
      if (res.status === 'success') {
        setPlans(res.data.plans);
        setTaxType(res.data.taxType); // Set CGST_SGST or IGST
      }
    } catch (error) {
      console.error("Failed to fetch plans", error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleOpenDiscountModal = (e: React.MouseEvent) => {
    e.preventDefault();
    // If a discount is already applied, pre-fill the modal with 'amount' type for simplicity
    if (customDiscount > 0 && !discountDetails.isCustomDiscountChecked) {
      setDiscountDetails({
        ...discountDetails,
        isCustomDiscountChecked: true,
        discountType: 'amount',
        discountValue: customDiscount
      });
    }
    setShowDiscountModal(true);
  };
  const applyCustomDiscount = () => {
    let calculatedDiscount = 0;
    if (discountDetails.isCustomDiscountChecked && discountDetails.discountValue !== '') {
      const value = Number(discountDetails.discountValue);
      if (discountDetails.discountType === 'amount') {
        calculatedDiscount = value;
      } else if (discountDetails.discountType === 'percentage') {
        // Calculate percentage based on the gross amount of the selected plan & term
        calculatedDiscount = (grossAmount * value) / 100;
      }
    }
    // Ensure discount doesn't exceed the taxable amount (gross - term discount)
    const maxDiscount = Math.max(0, grossAmount - termDiscount);
    setCustomDiscount(Math.min(calculatedDiscount, maxDiscount));
    setShowDiscountModal(false);
  };


  // --- NEW: API CALL TO SAVE PLAN ---
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        userId: user._id,
        planData: {
          plan_id: selectedPlan,
          billing_cycle: term,
          start_date: startDate,
          end_date: endDate,
          custom_discount: customDiscount,
          discount_reason: discountDetails.discountReason,
          total_amount: finalTotal,
          taxable_amount: taxableAmount
        }
      }; 
      const response = await postRequest('/user/assign-user-plan', payload);

      if (response.status === 'success') {
        showToast.success(response.message || "Plan assigned successfully!");
        onSuccess(); // Triggers parent component to refresh the user list
        onHide();    // Close the modal
      } else {
        showToast.error(response.message || "Failed to assign plan.");
      }
    } catch (error: any) {
      console.error("Error assigning plan:", error);
      showToast.error(error.message || "An error occurred while assigning the plan.");
    } finally {
      setIsSaving(false);
    }
  };;



  // Taxable Amount (Base - Plan Discount - Custom Discount)
  const taxableAmount = Math.max(0, grossAmount - termDiscount - customDiscount);

  // Exclusive Tax (18% added on top of the taxable amount)
  const taxAmount = taxableAmount * 0.18;

  // Final Total to be paid
  const finalTotal = taxableAmount + taxAmount;

  return (
    <>
      {/* --- MAIN MODAL --- */}
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Plan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {user ? (
            <Form>
              <div className="mb-3">
                <label className="form-label">User</label>
                <p className="form-control-plaintext fw-bold">
                  {user.user_first_name} {user.user_last_name} ({user.user_email})
                </p>
              </div>

              <div className="row">
                <div className="col-lg-6 mb-3">
                  <Form.Group>
                    <Form.Label>Select Plan</Form.Label>
                    <Form.Select
                      value={selectedPlan}
                      onChange={(e) => setSelectedPlan(e.target.value)}
                      disabled={loadingPlans || isSaving}
                    >
                      <option value="">{loadingPlans ? "Loading plans..." : "Select a plan..."}</option>
                      {plans.map((plan) => (
                        <option key={plan._id} value={plan._id}>
                          {plan.plan_title} (₹{plan.plan_selling_price}/mo)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-lg-6 mb-3">
                  <Form.Group>
                    <Form.Label>Billing Cycle</Form.Label>
                    <Form.Select value={term} onChange={(e) => setTerm(e.target.value)}>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-lg-6 mb-4">
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      disabled={isSaving}
                    />
                  </Form.Group>
                </div>

                <div className="col-lg-6 mb-4">
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={endDate}
                      disabled
                    />
                  </Form.Group>
                </div>

                {/* --- EXACT PROVIDED PAYMENT SECTION --- */}
                {selectedPlan && (
                  <div className="col-lg-12">
                    <div className="card">
                      <div className="card-header">
                        <h6>Payments</h6>
                      </div>
                      <div className="card-body py-2">
                        <ul className="payment-summary">
                          <li>
                            <span>Base Amount ({term})</span>
                            <span>₹{grossAmount.toFixed(2)}</span>
                          </li>

                          {/* 1. Plan Discount */}
                          <li>
                            <span>Plan Discount</span>
                            <span>- ₹{termDiscount.toFixed(2)}</span>
                          </li>

                          {/* 2. Custom Discount (Clickable to open modal) */}
                          <li>
                            <span>
                              <a
                                href="#"
                                onClick={isSaving ? undefined : handleOpenDiscountModal}
                              >
                                {customDiscount && customDiscount > 0 ? 'Edit Discount' : 'Add Discount'}
                              </a>
                            </span>
                            <span>- ₹{customDiscount.toFixed(2)}</span>
                          </li>

                          <li>
                            <span>Taxable Amount</span>
                            <span>₹{taxableAmount.toFixed(2)}</span>
                          </li>

                          {/* --- DYNAMIC TAX RENDERING --- */}
                          {taxType === 'CGST_SGST' ? (
                            <>
                              <li className="mb-1">
                                <span>CGST (9%)</span>
                                <span>₹{(taxAmount / 2).toFixed(2)}</span>
                              </li>
                              <li className="mb-3">
                                <span>SGST (9%)</span>
                                <span>₹{(taxAmount / 2).toFixed(2)}</span>
                              </li>
                            </>
                          ) : (
                            <li className="mb-3">
                              <span>IGST (18%)</span>
                              <span>₹{taxAmount.toFixed(2)}</span>
                            </li>
                          )}


                          <li>
                            <span className="fw-bold">Total</span>
                            <span className="fw-bold">₹{finalTotal.toFixed(2)}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Form>
          ) : (
            <p>Loading user data...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={isSaving}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!selectedPlan || !startDate || !endDate || isSaving}
          >
            {isSaving ? 'Assigning...' : 'Assign Plan'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --- NEW NESTED DISCOUNT MODAL --- */}
      <Modal show={showDiscountModal} onHide={() => setShowDiscountModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">{customDiscount && customDiscount > 0 ? 'Edit Discount' : 'Add Discount'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Static Coupon Code Field */}
            <Form.Group className="mb-3">
              <Form.Label>Discount codes</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter a discount code"
                disabled
              />
            </Form.Group>

            {/* Custom Discount Checkbox */}
            <Form.Group className="mb-3" controlId="customDiscountCheckbox">
              <Form.Check
                type="checkbox"
                label="Add custom order discount"
                checked={discountDetails.isCustomDiscountChecked}
                onChange={(e) => setDiscountDetails({ ...discountDetails, isCustomDiscountChecked: e.target.checked })}
              />
            </Form.Group>

            {/* Conditional Custom Discount Fields */}
            {discountDetails.isCustomDiscountChecked && (
              <>
                <Row className="mb-3">
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Discount type</Form.Label>
                      <Form.Select
                        value={discountDetails.discountType}
                        onChange={(e) => setDiscountDetails({ ...discountDetails, discountType: e.target.value as 'amount' | 'percentage' })}
                      >
                        <option value="amount">Amount</option>
                        <option value="percentage">Percentage</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={6}>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <InputGroup>
                        {discountDetails.discountType === 'amount' && <InputGroup.Text>₹</InputGroup.Text>}
                        <Form.Control
                          type="number"
                          min="0"
                          placeholder={discountDetails.discountType === 'amount' ? "0.00" : "0"}
                          value={discountDetails.discountValue}
                          onChange={(e) => setDiscountDetails({ ...discountDetails, discountValue: e.target.value === '' ? '' : Number(e.target.value) })}
                        />
                        {discountDetails.discountType === 'percentage' && <InputGroup.Text>%</InputGroup.Text>}
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-1">
                  <Form.Label>Reason for discount</Form.Label>
                  <Form.Control
                    type="text"
                    value={discountDetails.discountReason}
                    onChange={(e) => setDiscountDetails({ ...discountDetails, discountReason: e.target.value })}
                  />
                </Form.Group>
                <Form.Text className="text-muted">
                  Visible to customer
                </Form.Text>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDiscountModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={applyCustomDiscount}
            disabled={discountDetails.isCustomDiscountChecked && discountDetails.discountValue === ''}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AssignPlanModal;